import { ReactNode } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Logo } from '@/components/brand/logo'
import { Alert, AlertDescription } from '../ui/alert'
import { AlertTriangle } from 'lucide-react'

/**
 * Centered full-height container for authentication pages
 */
export function AuthContainer({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      {children}
    </div>
  )
}

/**
 * Card wrapper with consistent styling for auth pages
 */
interface AuthCardProps {
  title: string
  description: string
  children: ReactNode
}

export function AuthCard({ title, description, children }: AuthCardProps) {
  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  )
}

/**
 * Complete auth page layout combining container and card
 */
interface AuthLayoutProps {
  title: string
  description: string
  children: ReactNode
}

export function AuthLayout({ title, description, children }: AuthLayoutProps) {
  return (
    <AuthContainer>
      <div className="w-full max-w-md space-y-6">
        <div className="flex justify-center">
          <Logo className="h-8 w-auto" />
        </div>
        <AuthCard title={title} description={description}>
          {children}
        </AuthCard>

        <Alert
          variant="default"
          className="border-yellow-500/50 bg-yellow-50 dark:bg-yellow-950/20"
        >
          <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-500" />
          <AlertDescription className="text-sm text-yellow-800 dark:text-yellow-200">
            <strong>Prototype Notice:</strong> This application is for prototyping and testing
            purposes only. Data may be reset at any time and should not be considered permanent.
          </AlertDescription>
        </Alert>
      </div>
    </AuthContainer>
  )
}
